import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { defaultChartSettings } from "~/feature/chart/constants";
import { useChartChanceHistory } from "~/feature/chart/hooks/useChartChanceHistory";
import { useChartNews } from "~/feature/chart/hooks/useChartNews";
import { useChartVolume } from "~/feature/chart/hooks/useChartVolume";
import {
  type ChartSettingEnum,
  type ChartSettingsState,
  ChartViewEnum,
  EventChartRangeEnum,
  type IMarketGroupChanceHistory,
  type INewsMarker,
} from "~/feature/chart/types";
import {
  alignVolumeToTimeline,
  buildMarketGroups,
  dropUnavailableMarkets,
  getChartTimeline,
  getGroupedMarketIdsSelection,
  isWithinOneOutcome,
  toggleMarketSelection,
} from "~/feature/chart/utils";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";

interface ICoreChartProviderProps {
  children: ReactNode;
  event: IAggregatedEvent;
  markets: IMarket[];
  /** The markets endpoint's `outcomes[]` for the same markets: each outcome's PH Consensus. */
  outcomes: IOutcomeConsensus[];
}

interface ICoreChartContextType {
  event: IAggregatedEvent;
  markets: IMarket[];
  outcomes: IOutcomeConsensus[];
  marketGroups: Map<string, IMarket[]>;
  viewMode: ChartViewEnum;
  setViewMode: (viewMode: ChartViewEnum, marketId?: string) => void;
  range: EventChartRangeEnum;
  setRange: (range: EventChartRangeEnum) => void;
  selections: string[];
  setSelections: (marketId: string) => void;
  settings: ChartSettingsState;
  toggleSetting: (setting: ChartSettingEnum) => void;
  chanceHistory: IMarketGroupChanceHistory[];
  isChanceHistoryLoading: boolean;
  isChanceHistoryFetching: boolean;
  isChanceHistoryUnavailable: boolean;
  timeline: number[];
  volumeByTimeline: (number | null)[];
  isVolumeLoading: boolean;
  news: INewsMarker[];
  isNewsLoading: boolean;
}

const CoreChartContext = createContext<ICoreChartContextType | null>(null);

export const CoreChartProvider = ({
  children,
  event,
  markets,
  outcomes,
}: ICoreChartProviderProps) => {
  const [selections, setSelections] = useState<string[]>(() =>
    getGroupedMarketIdsSelection(markets)
  );
  const [range, setRange] = useState<EventChartRangeEnum>(
    EventChartRangeEnum.W1
  );
  const [viewMode, setViewMode] = useState<ChartViewEnum>(
    selections.length > 1 ? ChartViewEnum.CONSENSUS : ChartViewEnum.YES
  );
  const [settings, setSettings] = useState(defaultChartSettings);

  const {
    chanceHistory,
    isChanceHistoryLoading,
    isChanceHistoryFetching,
    isChanceHistoryUnavailable,
  } = useChartChanceHistory({
    selections,
    range,
  });
  const { volume, isVolumeLoading } = useChartVolume({ selections, range });
  const { news, isNewsLoading } = useChartNews({ selections, range });

  const handleSetSelection = useCallback(
    (marketId: string) => {
      setSelections((previousSelections) => {
        const nextSelections = toggleMarketSelection(
          previousSelections,
          marketId
        );
        const isConsensusBroken =
          viewMode === ChartViewEnum.CONSENSUS &&
          !isWithinOneOutcome(markets, nextSelections);

        if (isConsensusBroken) {
          setViewMode(ChartViewEnum.YES);
        }

        return nextSelections;
      });
    },
    [markets, viewMode]
  );

  const handleSetViewMode = useCallback(
    (viewMode: ChartViewEnum, marketId?: string) => {
      setViewMode(viewMode);

      if (viewMode === ChartViewEnum.CONSENSUS) {
        setSelections(
          getGroupedMarketIdsSelection(markets, marketId || selections[0])
        );

        return;
      }

      if (marketId) {
        setSelections([marketId]);
      }
    },
    [markets, selections]
  );

  const handleToggleSetting = useCallback((setting: ChartSettingEnum) => {
    setSettings((previousSettings) => ({
      ...previousSettings,
      [setting]: !previousSettings[setting],
    }));
  }, []);

  const marketGroups = useMemo(() => buildMarketGroups(markets), [markets]);

  const availableChanceHistory = useMemo(
    () =>
      dropUnavailableMarkets(chanceHistory, markets, {
        keepConsensus: viewMode === ChartViewEnum.CONSENSUS,
      }),
    [chanceHistory, markets, viewMode]
  );

  const timeline = useMemo(
    () => getChartTimeline(availableChanceHistory),
    [availableChanceHistory]
  );

  const volumeByTimeline = useMemo(
    () => alignVolumeToTimeline(timeline, volume),
    [timeline, volume]
  );

  const values = useMemo(
    () => ({
      event,
      markets,
      outcomes,
      marketGroups,
      viewMode,
      setViewMode: handleSetViewMode,
      range,
      setRange,
      selections,
      setSelections: handleSetSelection,
      settings,
      toggleSetting: handleToggleSetting,
      chanceHistory: availableChanceHistory,
      isChanceHistoryLoading,
      isChanceHistoryFetching,
      isChanceHistoryUnavailable,
      timeline,
      volumeByTimeline,
      isVolumeLoading,
      news,
      isNewsLoading,
    }),
    [
      event,
      handleSetSelection,
      handleSetViewMode,
      handleToggleSetting,
      availableChanceHistory,
      isChanceHistoryLoading,
      isChanceHistoryFetching,
      isChanceHistoryUnavailable,
      marketGroups,
      markets,
      outcomes,
      range,
      selections,
      settings,
      timeline,
      viewMode,
      volumeByTimeline,
      isVolumeLoading,
      news,
      isNewsLoading,
    ]
  );

  return (
    <CoreChartContext.Provider value={values}>
      {children}
    </CoreChartContext.Provider>
  );
};

export const useCoreChart = (): ICoreChartContextType => {
  const context = useContext(CoreChartContext);

  if (!context) {
    throw new Error("useCoreChart must be used within a CoreChartProvider");
  }

  return context;
};
